import { ReactNode } from "react";
import { Download, FileText, FileArchive, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  buildExportRangeMeta,
  ExportRangeMeta,
} from "@/pages/workshop/utils/dateRange";

export interface ExportRangeFormat<K extends string = string> {
  key: K;
  label: string;
  icon?: "csv" | "json" | "excel" | "zip";
  disabled?: boolean;
}

interface Props<K extends string = string> {
  presetLabel: string;
  range: { from: Date; to: Date } | null;
  onExport: (format: K) => void;
  trigger?: ReactNode;
  /**
   * Formatos de exportación a ofrecer. Por defecto CSV + JSON (compatibilidad
   * hacia atrás). Si se pasan, se muestran TODOS bajo el mismo resumen — así
   * Excel/CSV/JSON/ZIP comparten exactamente los mismos metadatos
   * (rango_desde_iso, rango_hasta_iso, zona_horaria, offset_min).
   */
  formats?: ExportRangeFormat<K>[];
  triggerLabel?: string;
}

const ICONS = {
  csv: Download,
  json: FileText,
  excel: FileSpreadsheet,
  zip: FileArchive,
} as const;

/**
 * Popover reutilizable que muestra el detalle exacto del rango a exportar
 * (rango_desde_iso, rango_hasta_iso, zona_horaria, offset_min) antes de
 * descargar el archivo. Garantiza que TODOS los formatos (CSV, JSON, Excel,
 * ZIP) usen idénticos metadatos del filtro vigente.
 */
export function ExportRangeSummary<K extends string = "csv" | "json">({
  presetLabel,
  range,
  onExport,
  trigger,
  formats,
  triggerLabel,
}: Props<K>) {
  const meta: ExportRangeMeta = buildExportRangeMeta(presetLabel, range);
  const finalFormats: ExportRangeFormat<K>[] = (formats ?? ([
    { key: "csv" as K, label: "CSV", icon: "csv" },
    { key: "json" as K, label: "JSON", icon: "json" },
  ] as ExportRangeFormat<K>[]));

  return (
    <Popover>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="h-9 gap-1.5 text-xs">
            <Download className="h-3.5 w-3.5" /> {triggerLabel ?? "Exportar"}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-3 bg-popover border-border z-50"
        align="end"
        role="region"
        aria-label="Resumen del rango a exportar"
      >
        <div className="space-y-3">
          <div className="text-xs font-medium" id="export-range-summary-title">Se exportará:</div>
          <div
            className="rounded-md border bg-muted/40 p-2 text-[11px] font-mono space-y-1 break-all"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            aria-labelledby="export-range-summary-title"
            data-testid="export-range-meta"
          >
            <div><span className="text-muted-foreground">periodo:</span> {meta.presetLabel}</div>
            <div><span className="text-muted-foreground">rango:</span> {meta.rangeLabel}</div>
            <div><span className="text-muted-foreground">rango_desde_iso:</span> {meta.fromISO ?? "—"}</div>
            <div><span className="text-muted-foreground">rango_hasta_iso:</span> {meta.toISO ?? "—"}</div>
            <div><span className="text-muted-foreground">zona_horaria:</span> {meta.timezone}</div>
            <div><span className="text-muted-foreground">offset_min:</span> {meta.timezoneOffsetMinutes}</div>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Formatos de exportación">
            {finalFormats.map((f) => {
              const Icon = ICONS[f.icon ?? "csv"];
              return (
                <Button
                  key={f.key}
                  variant="outline"
                  size="sm"
                  onClick={() => onExport(f.key)}
                  disabled={f.disabled}
                  className="flex-1 min-w-[80px] h-8 gap-1.5 text-xs"
                  data-export-format={f.key}
                >
                  <Icon className="h-3.5 w-3.5" /> {f.label}
                </Button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
